/* eslint-disable max-lines */
import { Overlay } from '@angular/cdk/overlay';
import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NavigationExtras, Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, Observable } from 'rxjs';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';
import { VendorWorkflowDirective } from '@features/feature-workflow/directives/vendor-workflow.directive';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SuggestionIconColor } from '@shared/components/modals/suggestions-confirmation-modal/suggestion.component';
import {
  SuggestionsConfirmationModalComponent,
  SuggestionsModalData,
} from '@shared/components/modals/suggestions-confirmation-modal/suggestions-confirmation-modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { TrimInputDirective } from '@shared/directives/trim-input.directive';
import { FileMetadata } from '@shared/services/api.service';
import {
  DocumentType,
  EntityType,
  EventType,
  GqlService,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { EXECUTABLE_BY_ROUTE_PARAM } from '@shared/utils/route.utils';
import { Utils } from '@shared/utils/utils';

export interface OrganizationDialogData {
  currentQuarterMonths?: string[];
  fetchFilesOnInit?: boolean;
  id?: string;
  skipAdditionalDialogs?: boolean;
  userHasPermission: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [VendorWorkflowDirective, InvoiceWorkflowDirective],
  imports: [
    NgSelectModule,
    InputComponent,
    CommonModule,
    ReactiveFormsModule,
    FormErrorDirective,
    TooltipDirective,
    FormErrorDirective,
    AsyncPipe,
    FileManagerComponent,
    FileViewerComponent,
    CheckboxComponent,
    TrimInputDirective,
  ],
  providers: [VendorWorkflowDirective],
  selector: 'aux-organization-dialog',
  templateUrl: './organization-dialog.component.html',
})
export class OrganizationDialogComponent implements AfterViewInit, OnInit {
  organizationQuery = inject(OrganizationQuery);
  ref = inject(CustomOverlayRef<{ vendorId: string }, OrganizationDialogData>);
  router = inject(Router);
  vendorsQuery = inject(OrganizationQuery);

  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private formBuilder = inject(UntypedFormBuilder);
  private gqlService = inject(GqlService);
  private readonly invoiceCtx = inject(InvoiceWorkflowDirective);
  private launchDarklyService = inject(LaunchDarklyService);
  private ld = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private overlay = inject(Overlay);
  private overlayService = inject(OverlayService);
  private readonly vendorCtx = inject(VendorWorkflowDirective);
  private workflowQuery = inject(WorkflowQuery);

  contractHasBeenUploaded = signal<boolean>(false);
  filesRemoved = signal<boolean>(false);
  hasFormChanges = signal<boolean>(false);
  isThereAnyChangesToFiles = signal<boolean>(false);
  otherHasBeenUploaded = signal<boolean>(false);
  proposalHasBeenUploaded = signal<boolean>(false);

  private hasNoEditBudgetPermission = signal(false);
  private hasPOPermission = signal(false);

  private timelineExist = toSignal(this.mainQuery.selectTimelineExist());

  hasChanges = computed(() => {
    return this.hasFormChanges() || this.isThereAnyChangesToFiles() || this.filesRemoved();
  });
  saveBtnTooltip = computed(() => {
    return this.hasChanges() ? '' : 'No changes to save.';
  });

  isChangeOrdersWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CHANGE_ORDERS
  );

  private addBudgetSuggestionDisabled = computed(() => {
    return (
      this.isChangeOrdersWorkflowLocked() ||
      !this.timelineExist() ||
      this.hasNoEditBudgetPermission()
    );
  });

  private featureFlagBudgetNav = this.launchDarklyService.$select((flags) => flags.nav_budget);

  private addBudgetSuggestionHidden = computed(() => {
    return !this.featureFlagBudgetNav();
  });
  private lockedForPeriodCloseTooltip = computed(() => {
    if (this.isChangeOrdersWorkflowLocked()) {
      return MessagesConstants.LOCKED_FOR_PERIOD_CLOSE;
    }

    return '';
  });
  private addBudgetTooltip = computed(() => {
    if (this.hasNoEditBudgetPermission()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    if (!this.timelineExist()) {
      return MessagesConstants.TIMELINE_MUST_BE_ENTERED;
    }

    return this.lockedForPeriodCloseTooltip();
  });

  private featureFlagInvoicesTab = this.launchDarklyService.$select((flags) => flags.tab_invoices);

  private addInvoiceSuggestionHidden = computed(() => {
    return !this.featureFlagInvoicesTab();
  });
  private addPODisabled = computed(() => {
    return !this.hasPOPermission() || this.isChangeOrdersWorkflowLocked();
  });

  private featureFlagPOtab = this.launchDarklyService.$select((flags) => flags.tab_purchase_orders);

  private addPoSuggestionHidden = computed(() => {
    return !this.featureFlagPOtab() || !this.featureFlagBudgetNav();
  });
  private addPOTooltip = computed(() => {
    if (!this.hasPOPermission()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return this.lockedForPeriodCloseTooltip();
  });

  private featureFlagVendorsTab = this.launchDarklyService.$select((flags) => flags.tab_vendors);

  private addVendorSuggestionHidden = computed(() => {
    return !this.featureFlagBudgetNav() || !this.featureFlagVendorsTab();
  });

  readonly currencyOptions = Utils.CURRENCY_OPTIONS;
  currentOpenMonth = '';
  currentQuarterMonths: string[] = [];
  @ViewChild('description') description!: TemplateRef<unknown>;
  DOCUMENT_OTHER = DocumentType.DOCUMENT_OTHER;
  DOCUMENT_PROPOSAL = DocumentType.DOCUMENT_PROPOSAL;
  DOCUMENT_VENDOR_CONTRACT = DocumentType.DOCUMENT_VENDOR_CONTRACT;
  fetchFilesOnInit = true;
  fg = this.formBuilder.group({
    contact_id: '',
    costs_included_in_parent_wo: false,
    currency: [null, Validators.required],
    email: ['', CustomValidators.emailValidator()],
    family_name: '',
    given_name: '',
    is_third_party: false,
    parent_organization_id: '',
    phone_number: '',
    receives_ve: false,
    receives_vendor_estimate: false,
    title: '',
    vendor_type: ['', Validators.required],
  });
  @ViewChild('fileManager') fileManager: FileManagerComponent | undefined;
  @ViewChild('fileManager2') fileManager2: FileManagerComponent | undefined;
  @ViewChild('fileManager3') fileManager3: FileManagerComponent | undefined;
  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  initialFgValue = this.fg.value;
  vendorFc = new UntypedFormControl();
  initialVendorVal = this.vendorFc.value;
  isInvoicesLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES
  );
  isVendorEstimateExists = false;
  loading$ = new BehaviorSubject(false);
  mode: 'add' | 'edit' = 'edit';
  ORGANIZATION = EntityType.ORGANIZATION;
  permissionMessage = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  suggestionsModal: CustomOverlayRef | undefined;
  userHasPermission = false;
  vendorCurrencyEnabled$: Observable<boolean>;
  vendorId: null | string = null;
  vendorNameValidators: ValidatorFn[] = [
    (control) => {
      const value: string = control.value;

      if (!value) {
        return { required: true };
      }

      const vendor = this.vendorsQuery
        .getAll()
        .find((vendor) => vendor.name?.toLowerCase() === value.toLowerCase());

      return vendor && vendor.id !== this.vendorId ? { duplicateVendor: true } : null;
    },
  ];
  readonly vendorsRoutingPath = `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.VENDORS}`;
  vendorTypeOptions = Object.entries(Utils.VendorTypeEnum).map(([key, label]) => ({
    key,
    label,
  }));

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async ngOnInit() {
    this.mainQuery
      .select('currentOpenMonth')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currentOpenMonth) => {
        this.currentOpenMonth = dayjs(currentOpenMonth).format('MMM-YYYY').toUpperCase();
      });

    this.mode = 'add';

    if (this.ref.data?.id) {
      this.mode = 'edit';
      this.vendorId = this.ref.data.id;
      this.currentQuarterMonths = [...(this.ref.data.currentQuarterMonths || [])];

      const org = this.organizationQuery.getEntity(this.vendorId);

      if (org) {
        const { currency, id, name, parent_organization, receives_vendor_estimate, vendor_type } =
          org;

        this.loading$.next(true);
        const { data, success } = await firstValueFrom(this.organizationService.getContacts(id));

        if (success && data) {
          this.fg.patchValue({
            ...data,
            contact_id: data.id,
            costs_included_in_parent_wo: org.costs_included_in_parent_wo,
            currency,
            name,
            parent_organization_id: parent_organization?.id,
            receives_vendor_estimate,
            vendor_type: vendor_type,
          });
          this.fg.markAsUntouched({ onlySelf: true });
          this.refreshVendorValidators();
        } else {
          this.fg.patchValue({
            costs_included_in_parent_wo: org.costs_included_in_parent_wo,
            currency,
            name,
            parent_organization_id: parent_organization?.id,
            receives_vendor_estimate,
            vendor_type: vendor_type,
          });
        }

        this.vendorFc.setValue(org.name);
        this.initialVendorVal = org.name;

        if (org.baseline_budget_version) {
          this.fg.get('currency')?.disable();
        }

        await this.initVendorEstimateExist();

        this.loading$.next(false);
      } else {
        this.overlayService.error('User not found!');
      }
    }
  }

  ngAfterViewInit(): void {
    this.checkFileChanges();
  }

  checkChangesFromForm() {
    if (this.fg.pristine) {
      this.initialFgValue = this.fg.value;
    }

    if (this.vendorFc.pristine) {
      this.initialVendorVal = this.vendorFc.value;
    }

    const fgControl = !Object.keys(this.initialFgValue).every((key) => {
      const fgValue = this.fg.value[key] ? this.fg.value[key] : null;
      const initialValue = this.initialFgValue[key] ? this.initialFgValue[key] : null;

      return fgValue === initialValue;
    });
    const vendorNameControl = !!(
      this.initialVendorVal !== this.vendorFc.value && this.vendorFc.value !== null
    );

    this.hasFormChanges.set(fgControl || vendorNameControl);
  }

  checkFileChanges() {
    combineLatest(
      this.fileManager?.fileQuery.selectAll() || EMPTY,
      this.fileManager2?.fileQuery.selectAll() || EMPTY,
      this.fileManager3?.fileQuery.selectAll() || EMPTY
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([contracts, proposals, others]) => {
        const isThereNewContract = contracts.filter((contract) => !contract.uploaded).length > 0;
        const isThereNewProposal = proposals.filter((proposal) => !proposal.uploaded).length > 0;
        const isThereNewOthers = others.filter((other) => !other.uploaded).length > 0;

        this.isThereAnyChangesToFiles.set(
          isThereNewContract || isThereNewProposal || isThereNewOthers
        );
      });
  }

  constructor() {
    this.fetchFilesOnInit = this.ref.data?.fetchFilesOnInit ?? this.fetchFilesOnInit;
    this.userHasPermission = this.ref.data?.userHasPermission ?? this.userHasPermission;
    this.vendorCurrencyEnabled$ = this.ld.select$((flags) => flags.vendor_currency);
    this.fg.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.checkChangesFromForm();
    });
    this.vendorFc.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkChangesFromForm());
    this.watchPermissions();
  }

  getFc(str: string) {
    return this.fg.get(str);
  }

  getFilePaths(type: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return () => (this.vendorId ? `trials/${trialId}/vendors/${this.vendorId}/${type}/` : '');
  }

  getMetadata(): () => FileMetadata {
    return () => ({});
  }

  // eslint-disable-next-line max-lines-per-function
  private getSuggestionsData(): SuggestionsModalData {
    const navigationExtras: NavigationExtras = {
      queryParams: { modalVendorId: this.vendorId },
    };
    const suggestedSteps = [
      {
        action: () => {
          void this.router.navigate(
            [`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.PURCHASE_ORDERS}`],
            navigationExtras
          );
        },
        disabled: this.addPODisabled,
        hidden: this.addPoSuggestionHidden,
        iconColor: SuggestionIconColor.GREEN,
        iconName: 'Plus',
        name: 'Add Purchase Order',
        pendoTag: 'add-vendor-modal-add-po',
        tooltip: this.addPOTooltip,
      },
      {
        action: () => {
          localStorage.removeItem('selectedVendor');
          void this.router.navigate(
            [`/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.INDEX}`],
            navigationExtras
          );
        },
        disabled: this.addBudgetSuggestionDisabled,
        hidden: this.addBudgetSuggestionHidden,
        iconColor: SuggestionIconColor.GREEN,
        iconName: 'FileInvoice',
        name: 'Add Vendor Budget',
        pendoTag: 'add-vendor-modal-add-budget',
        tooltip: this.addBudgetTooltip,
      },
      {
        action: () => {
          void this.router.navigate(
            [
              `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`,
            ],
            navigationExtras
          );
        },
        disabled: this.invoiceCtx.disabledAddInvoiceUI,
        hidden: this.addInvoiceSuggestionHidden,
        iconColor: SuggestionIconColor.LIGHT_BLUE,
        iconName: 'FileInvoice',
        name: 'Add Invoice',
        pendoTag: 'add-vendor-modal-add-invoice',
        tooltip: this.invoiceCtx.invoiceLockTooltip,
      },
      {
        action: () => {
          void this.router.navigate([this.vendorsRoutingPath], {
            queryParams: { [EXECUTABLE_BY_ROUTE_PARAM]: true },
          });
        },
        disabled: this.vendorCtx.disabledUI,
        hidden: this.addVendorSuggestionHidden,
        iconColor: SuggestionIconColor.PURPLE,
        iconName: 'User',
        name: 'Add Another Vendor Contract',
        pendoTag: 'add-vendor-modal-add-vendor-contract',
        tooltip: this.vendorCtx.vendorActionTooltip,
      },
    ];

    return {
      // Discussed with Jeremy, commented out for the future
      // description: this.description,
      suggestedSteps,
    };
  }

  private async initVendorEstimateExist(): Promise<void> {
    if (this.vendorId) {
      const currentOpenMonthIndex = this.currentQuarterMonths.indexOf(this.currentOpenMonth);
      const monthsForCheck = this.currentQuarterMonths.slice(currentOpenMonthIndex);

      for (const month of monthsForCheck) {
        if (!this.isVendorEstimateExists) {
          const result = await firstValueFrom(this.gqlService.listVendorEstimateSummaries$(month));

          // eslint-disable-next-line max-depth
          if (this.vendorId && result.success && result.data) {
            this.isVendorEstimateExists = !!result.data.find(
              (vendor) => vendor.organization_id === this.vendorId
            )?.vendor_estimate_exists;
          }
        }
      }
    }
  }

  onCancel() {
    this.ref.close({ vendorId: this.vendorId || '' });
  }

  onFilesAdded(fileType: string) {
    if (this.fileManager && fileType === 'contracts') {
      this.contractHasBeenUploaded.set(true);
    }

    if (this.fileManager2 && fileType === 'proposals') {
      this.proposalHasBeenUploaded.set(true);
    }

    if (this.fileManager3 && fileType === 'other') {
      this.otherHasBeenUploaded.set(true);
    }
  }

  onFilesRemoved() {
    this.filesRemoved.set(true);
  }

  // eslint-disable-next-line max-lines-per-function
  async onSubmit() {
    this.vendorFc.markAsTouched();
    this.vendorFc.updateValueAndValidity();

    Object.keys(this.fg.controls).forEach((key) => {
      this.fg.get(key)?.markAsTouched();
      this.fg.get(key)?.updateValueAndValidity();
    });

    if (this.vendorFc.value && this.fg.valid && this.vendorFc.valid) {
      this.loading$.next(true);

      if (this.mode === 'add') {
        const {
          costs_included_in_parent_wo,
          currency,
          email,
          family_name,
          given_name,
          parent_organization_id,
          phone_number,
          receives_vendor_estimate,
          title,
          vendor_type,
        } = this.fg.value;
        const { data, errors, success } = await this.organizationService.add(
          this.vendorFc.value,
          {
            costs_included_in_parent_wo,
            email,
            family_name,
            given_name,
            parent_organization_id,
            phone_number,
            receives_vendor_estimate,
            title,
            vendor_type,
          },
          currency || null
        );

        if (success && data) {
          this.vendorId = data.id;
          await this.processFiles(this.vendorId);

          // eslint-disable-next-line max-depth
          if (!this.ref.data?.skipAdditionalDialogs) {
            this.openSuccessConfirmationModal();
          }

          this.ref.close({ vendorId: this.vendorId });
        } else {
          this.overlayService.error(errors);
        }
      } else if (this.vendorId) {
        const {
          costs_included_in_parent_wo,
          currency,
          email,
          family_name,
          given_name,
          parent_organization_id,
          phone_number,
          receives_vendor_estimate,
          title,
          vendor_type,
        } = this.fg.value;

        const { errors, success } = await this.organizationService.update(
          this.vendorId,
          this.vendorFc.value,
          {
            costs_included_in_parent_wo,
            email,
            family_name,
            given_name,
            parent_organization_id,
            phone_number,
            receives_vendor_estimate,
            title,
            vendor_type,
          },
          currency || null
        );

        await this.processFiles(this.vendorId);

        if (success) {
          this.overlayService.success('Vendor Contract successfully updated!');
          this.ref.close({ vendorId: this.vendorId });
        } else {
          this.overlayService.error(errors);
        }
      }
    }

    this.loading$.next(false);
  }

  openSuccessConfirmationModal(): void {
    this.suggestionsModal = this.overlayService.openPopup({
      content: SuggestionsConfirmationModalComponent,
      data: this.getSuggestionsData(),
      settings: {
        backdropClass: ['ttt'],
        header: 'Vendor Contract Added Successfully',
        headerIcon: { color: SuggestionIconColor.GREEN, name: 'CircleCheck' },
        hideActions: true,
        panelClasses: ['suggestions-confirmation'],
        positionStrategy: this.overlay.position().global().right('32px').top('80px'),
      },
    });
  }

  async processFiles(vendor_id: string) {
    await this.upload(this.fileManager, 'contracts', vendor_id);
    await this.upload(this.fileManager2, 'proposals', vendor_id);
    await this.upload(this.fileManager3, 'other', vendor_id);
  }

  refreshVendorValidators(): void {
    const controlNames = ['given_name', 'family_name', 'email', 'title', 'phone_number'];
    const vendorFieldsEdited =
      controlNames.some((name) => {
        const ctl = this.fg.get(name);

        ctl?.markAsTouched({ onlySelf: true });

        return ctl?.value;
      }) || this.fg.get('contact_id')?.value;

    for (const name of ['given_name', 'family_name']) {
      const ctl = this.fg.get(name);
      const validators = vendorFieldsEdited ? [Validators.required] : [];

      ctl?.setValidators(validators);
      ctl?.updateValueAndValidity();
    }
  }

  upload = async (
    fileManager: FileManagerComponent | undefined,
    fileType: string,
    vendor_id: string
  ) => {
    if (fileManager) {
      const path = this.getFilePaths(fileType);
      const files = fileManager.fileQuery.getAll();
      let fileNames = '';

      for (const file of files) {
        fileNames += `${file.fileName} `;
        fileManager.fileStore.update(file.id, {
          ...file,
          bucket_key: `${path()}${file.bucket_key}`,
        });
      }

      const currentFiles = fileManager.fileStore.getValue().entities;
      let documentType = DocumentType.DOCUMENT_OTHER;
      let aNewFileWasUploaded = false;

      if (fileType === 'contracts') {
        documentType = DocumentType.DOCUMENT_VENDOR_CONTRACT;

        if (currentFiles) {
          aNewFileWasUploaded = this.contractHasBeenUploaded();
        }
      }

      if (fileType === 'proposals') {
        documentType = DocumentType.DOCUMENT_PROPOSAL;

        if (currentFiles) {
          aNewFileWasUploaded = this.proposalHasBeenUploaded();
        }
      }

      if (fileType === 'other') {
        documentType = DocumentType.DOCUMENT_OTHER;

        if (currentFiles) {
          aNewFileWasUploaded = this.otherHasBeenUploaded();
        }
      }

      const success = await fileManager.fileService.uploadFiles({
        insertDocument: true,
        metadata: {
          documentType,
          entity_id: vendor_id,
          entity_type_id: EntityType.ORGANIZATION,
          vendor: vendor_id,
        },
        showSuccess: false,
      });

      if (success && !this.authQuery.isAuxAdmin() && aNewFileWasUploaded && fileNames.length > 0) {
        await firstValueFrom(
          this.eventService.processEvent$({
            entity_id: vendor_id,
            entity_type: EntityType.ORGANIZATION,
            payload: JSON.stringify({
              document_type: documentType,
              file_name: fileNames,
              uploaded_by: `${this.authQuery.getFullName()} ${this.authQuery.getEmail()}`,
            }),
            type: EventType.VENDOR_DOCUMENT_UPLOADED_NOTIFICATION,
          })
        );
      }
    }
  };

  private watchPermissions(): void {
    combineLatest([
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_UPDATE_PURCHASE_ORDERS],
        sysAdminsOnly: false,
      }),
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_EDIT_BUDGET],
        sysAdminsOnly: false,
      }),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([POPermission, editBudgetPermission]) => {
        this.hasPOPermission.set(POPermission);
        this.hasNoEditBudgetPermission.set(!editBudgetPermission);
      });
  }
}
